import { safeFetchAPI } from '@/api';
import { log, utils } from '@/helpers';
import {
	CoMarketingEditRequestWithTemplateAndCampaign,
	GetMostRecentCoMarketingEditReqFilters,
	InstalledCoMarketingTemplateWithCampaign,
	PaginatedData,
	Pagination,
	SponsoredCoMarketingTemplateWithCampaign,
} from '@/types';
import { create } from 'zustand';

type Paginator = Pagination | ((pagination: Pagination) => Pagination);

type LoadingMode = 'light' | 'full';

export type NeedsAttentionStoreState = {
	loaded: boolean;
	loading: boolean;
	loadingMode: LoadingMode;
	editRequests: CoMarketingEditRequestWithTemplateAndCampaign[];
	waitingToBeApprovedTemplates: InstalledCoMarketingTemplateWithCampaign[];
	sponsoredTemplates: SponsoredCoMarketingTemplateWithCampaign[];
	totalSponsoredTemplates: number;

	combinedItems: CombinedItem[];

	// Pagination for combined items (client-side)
	combinedPagination: {
		page: number;
		limit: number;
		totalItems: number;
	};

	// Track individual endpoint pagination states
	endpointsState: {
		editRequests: { page: number; hasMore: boolean; totalResult?: number };
		waitingToBeApproved: { page: number; hasMore: boolean; totalResult?: number };
		sponsored: { page: number; hasMore: boolean; totalResult?: number };
	};
} & Pagination;

type CombinedItem = CoMarketingEditRequestWithTemplateAndCampaign | InstalledCoMarketingTemplateWithCampaign | SponsoredCoMarketingTemplateWithCampaign;

export type NeedsAttentionStoreActions = {
	initialize: (mode?: LoadingMode) => Promise<void>;
	loadFullData: () => Promise<void>;

	fetchOpenEditRequests: (pagination?: Paginator, init?: boolean) => Promise<CoMarketingEditRequestWithTemplateAndCampaign[]>;
	fetchWaitingToBeApprovedTemplates: (pagination?: Paginator, init?: boolean) => Promise<InstalledCoMarketingTemplateWithCampaign[]>;
	fetchSponsoredTemplates: (pagination?: Paginator, init?: boolean) => Promise<SponsoredCoMarketingTemplateWithCampaign[]>;

	// Client-side pagination for combined items
	setCombinedPagination: (pagination: Partial<{ page: number; limit: number }>) => void;
	loadMoreIfNeeded: (targetPage: number) => Promise<void>;

	// Legacy methods (kept for compatibility)
	nextPage: () => void;
	previousPage: () => void;
};

export type NeedsAttentionStore = NeedsAttentionStoreState & NeedsAttentionStoreActions;

const initialState: NeedsAttentionStoreState = {
	loaded: false,
	loading: false,
	loadingMode: 'light',
	editRequests: [],
	waitingToBeApprovedTemplates: [],
	sponsoredTemplates: [],
	totalSponsoredTemplates: 0,
	page: 1,
	limit: 10,
	combinedItems: [],
	combinedPagination: {
		page: 1,
		limit: 10,
		totalItems: 0,
	},
	endpointsState: {
		editRequests: { page: 0, hasMore: true },
		waitingToBeApproved: { page: 0, hasMore: true },
		sponsored: { page: 0, hasMore: true },
	},
};

const forceToken = '';

export const useNeedsAttentionStore = create<NeedsAttentionStore>((set, get) => ({
	...initialState,

	initialize: async (mode: LoadingMode = 'light') => {
		if (get().loading) {
			return;
		}

		set({ loading: true, loadingMode: mode });

		// For light mode, fetch just first page of each endpoint (for cards)
		// For full mode, fetch more data (for table view)
		const lightModeLimit = 5; // Small limit for card view
		const fullModeLimit = 20; // Larger limit for table view
		const limit = mode === 'light' ? lightModeLimit : fullModeLimit;

		const editRequests = await get().fetchOpenEditRequests({ page: 1, limit }, true);
		const waitingToBeApprovedTemplates = await get().fetchWaitingToBeApprovedTemplates({ page: 1, limit }, true);
		const sponsoredTemplates = await get().fetchSponsoredTemplates({ page: 1, limit }, true);

		const combinedItems = ([...editRequests, ...waitingToBeApprovedTemplates, ...sponsoredTemplates] as CombinedItem[]).sort((a, b) => {
			return new Date(b.updated).getTime() - new Date(a.updated).getTime();
		});

		set({
			combinedItems,
			loaded: true,
			loading: false,
			combinedPagination: {
				...get().combinedPagination,
				totalItems: combinedItems.length,
			},
		});
	},

	loadFullData: async () => {
		if (get().loadingMode === 'full') {
			return; // Already in full mode
		}

		await get().initialize('full');
	},

	setCombinedPagination: (pagination) => {
		set({
			combinedPagination: {
				...get().combinedPagination,
				...pagination,
			},
		});
	},

	loadMoreIfNeeded: async (targetPage: number) => {
		const { combinedPagination, combinedItems, endpointsState } = get();
		const itemsNeeded = targetPage * combinedPagination.limit;

		// If we have enough items, no need to load more
		if (combinedItems.length >= itemsNeeded) {
			return;
		}

		// Check if any endpoint has more data to load
		const canLoadMore = endpointsState.editRequests.hasMore || endpointsState.waitingToBeApproved.hasMore || endpointsState.sponsored.hasMore;

		if (!canLoadMore) {
			return; // No more data available
		}

		set({ loading: true });

		// Load more from each endpoint that has more data
		const promises = [];

		if (endpointsState.editRequests.hasMore) {
			promises.push(
				get().fetchOpenEditRequests({
					page: endpointsState.editRequests.page + 1,
					limit: 10,
				}),
			);
		}

		if (endpointsState.waitingToBeApproved.hasMore) {
			promises.push(
				get().fetchWaitingToBeApprovedTemplates({
					page: endpointsState.waitingToBeApproved.page + 1,
					limit: 10,
				}),
			);
		}

		if (endpointsState.sponsored.hasMore) {
			promises.push(
				get().fetchSponsoredTemplates({
					page: endpointsState.sponsored.page + 1,
					limit: 10,
				}),
			);
		}

		await Promise.all(promises);

		// Recombine and sort all items
		const { editRequests, waitingToBeApprovedTemplates, sponsoredTemplates } = get();
		const newCombinedItems = ([...editRequests, ...waitingToBeApprovedTemplates, ...sponsoredTemplates] as CombinedItem[]).sort((a, b) => {
			return new Date(b.updated).getTime() - new Date(a.updated).getTime();
		});

		set({
			combinedItems: newCombinedItems,
			loading: false,
			combinedPagination: {
				...get().combinedPagination,
				totalItems: newCombinedItems.length,
			},
		});
	},

	fetchOpenEditRequests: async (pagination?: Paginator, init?: boolean) => {
		const current = { page: get().endpointsState.editRequests.page + 1, limit: get().limit };
		const paginator = (typeof pagination === 'function' ? pagination({ page: current.page, limit: current.limit }) : pagination) ?? current;

		if (!init) {
			set({ loading: true });
		}

		const [changeRequests, error] = await safeFetchAPI<PaginatedData<CoMarketingEditRequestWithTemplateAndCampaign>>('/co-marketing/installed/edit/:uid/all', {
			method: 'POST',
			payload: {
				includeCampaign: true,
				page: paginator.page || 1,
				limit: paginator.limit || 10,
				userID: utils.uid,
				antiApprovalStatus: 'approved',
				includeTemplate: true,
			} as GetMostRecentCoMarketingEditReqFilters,
			forceToken,
		});

		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error fetching edit requests',
				type: 'danger',
			});
			if (!init) {
				set({ loading: false });
			}
			return [];
		}

		const newEditRequests = changeRequests?.data ?? [];
		const currentEditRequests = get().editRequests;

		// Append new data if it's a subsequent page, replace if it's first page
		const updatedEditRequests = paginator.page === 1 ? newEditRequests : [...currentEditRequests, ...newEditRequests];

		// Update endpoint state
		const totalResult = changeRequests?.pagination?.totalResult ?? 0;
		const hasMore = updatedEditRequests.length < totalResult;

		set({
			editRequests: updatedEditRequests,
			endpointsState: {
				...get().endpointsState,
				editRequests: {
					page: paginator.page,
					hasMore,
					totalResult,
				},
			},
		});

		log('editRequests', updatedEditRequests);
		if (!init) {
			set({ loading: false });
		}
		return newEditRequests;
	},

	fetchWaitingToBeApprovedTemplates: async (pagination?: Paginator, init?: boolean) => {
		const current = { page: get().endpointsState.waitingToBeApproved.page + 1, limit: get().limit };
		const paginator = (typeof pagination === 'function' ? pagination({ page: current.page, limit: current.limit }) : pagination) ?? current;

		if (!init) {
			set({ loading: true });
		}

		const [waitingToBeApproved, error] = await safeFetchAPI<PaginatedData<InstalledCoMarketingTemplateWithCampaign>>('/co-marketing/installed/:uid/unactivated', {
			method: 'GET',
			queryParams: {
				includeCampaign: true,
				page: paginator.page || 1,
				limit: paginator.limit || 10,
			} as GetMostRecentCoMarketingEditReqFilters,
			forceToken,
		});

		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error fetching waiting to be approved templates',
				type: 'danger',
			});
			if (!init) {
				set({ loading: false });
			}
			return [];
		}

		const newWaitingTemplates = waitingToBeApproved?.data ?? [];
		const currentWaitingTemplates = get().waitingToBeApprovedTemplates;

		// Append new data if it's a subsequent page, replace if it's first page
		const updatedWaitingTemplates = paginator.page === 1 ? newWaitingTemplates : [...currentWaitingTemplates, ...newWaitingTemplates];

		// Update endpoint state
		const totalResult = waitingToBeApproved?.pagination?.totalResult ?? 0;
		const hasMore = updatedWaitingTemplates.length < totalResult;

		set({
			waitingToBeApprovedTemplates: updatedWaitingTemplates,
			endpointsState: {
				...get().endpointsState,
				waitingToBeApproved: {
					page: paginator.page,
					hasMore,
					totalResult,
				},
			},
		});

		log('waitingToBeApproved', updatedWaitingTemplates);
		if (!init) {
			set({ loading: false });
		}
		return newWaitingTemplates;
	},

	fetchSponsoredTemplates: async (pagination?: Paginator, init?: boolean) => {
		const current = { page: get().endpointsState.sponsored.page + 1, limit: get().limit };
		const paginator = (typeof pagination === 'function' ? pagination({ page: current.page, limit: current.limit }) : pagination) ?? current;

		if (!init) {
			set({ loading: true });
		}

		const [templates, error] = await safeFetchAPI<PaginatedData<SponsoredCoMarketingTemplateWithCampaign>>('/co-marketing/sponsored/:uid', {
			queryParams: {
				paid: true,
				includeCampaign: true,
				page: paginator.page || 1,
				limit: paginator.limit || 10,
			},
			forceToken,
		});

		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error fetching sponsored templates',
				type: 'danger',
			});
			if (!init) {
				set({ loading: false });
			}
			return [];
		}

		const newSponsoredTemplates = templates?.data ?? [];
		const currentSponsoredTemplates = get().sponsoredTemplates;

		// Append new data if it's a subsequent page, replace if it's first page
		const updatedSponsoredTemplates = paginator.page === 1 ? newSponsoredTemplates : [...currentSponsoredTemplates, ...newSponsoredTemplates];

		// Update endpoint state
		const totalResult = templates?.pagination?.totalResult ?? 0;
		const hasMore = updatedSponsoredTemplates.length < totalResult;

		set({
			sponsoredTemplates: updatedSponsoredTemplates,
			totalSponsoredTemplates: totalResult,
			endpointsState: {
				...get().endpointsState,
				sponsored: {
					page: paginator.page,
					hasMore,
					totalResult,
				},
			},
		});

		log('sponsoredTemplates', updatedSponsoredTemplates);
		if (!init) {
			set({ loading: false });
		}
		return newSponsoredTemplates;
	},

	// Legacy methods (kept for compatibility but now work with combined pagination)
	nextPage: () => {
		const { combinedPagination } = get();
		const maxPage = Math.ceil(combinedPagination.totalItems / combinedPagination.limit);
		if (combinedPagination.page < maxPage) {
			get().setCombinedPagination({ page: combinedPagination.page + 1 });
		}
	},

	previousPage: () => {
		const { combinedPagination } = get();
		if (combinedPagination.page > 1) {
			get().setCombinedPagination({ page: combinedPagination.page - 1 });
		}
	},
}));
